import { useGetAllProducts } from "@/api/queries";
import { ProductCard } from "@/components/atoms";
import { Button } from "@/components/ui";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useMemo } from "react";
import { getProductAvailability } from "@/lib/utils";

export const NewStock = () => {
  const { data, isFetching, isLoading } = useGetAllProducts();
  const navigate = useNavigate();

  // Get random products that are in stock
  const displayProducts = useMemo(() => {
    if (!data?.pages?.[0]?.data?.data?.products) return [];

    // Filter products that are in stock (totalStock > 0)
    const inStockProducts = data.pages[0].data.data.products.filter(
      (product) => product.totalStock > 0,
    );

    // Shuffle the array to get random products
    const shuffledProducts = [...inStockProducts].sort(() => Math.random() - 0.5);

    // Return first 10 random in-stock products
    return shuffledProducts.slice(0, 10);
  }, [data]);

  const handleSeeAll = () => {
    navigate("/shop");
  };

  // Loading state
  if (isLoading) {
    return (
      <section className='py-10 px-4 md:px-8 mb-12'>
        <div className='container mx-auto'>
          <div className='flex justify-between items-center mb-6'>
            <h2 className='text-2xl font-bold text-gray-800'>New Products In Stock</h2>
            <Button
              onClick={handleSeeAll}
              variant='outline'
              className='text-primary border-primary hover:bg-primary hover:text-white'
            >
              See All Products
            </Button>
          </div>

          <div className='flex justify-center items-center py-12'>
            <div className='flex items-center space-x-2'>
              <Loader2 className='h-6 w-6 animate-spin' />
              <span>Loading new products...</span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // If no products found
  if (!displayProducts.length) {
    return (
      <section className='py-10 px-4 md:px-8 mb-2'>
        <div className='container mx-auto'>
          <div className='flex justify-between items-center mb-6'>
            <h2 className='text-2xl font-bold text-gray-800'>New Products In Stock</h2>
            <Button
              onClick={handleSeeAll}
              variant='outline'
              className='text-primary border-primary hover:bg-primary hover:text-white'
            >
              See All Products
            </Button>
          </div>

          <div className='text-center py-12'>
            <p className='text-gray-600'>No products in stock at the moment.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className='py-4 px-4 md:px-8 mb-12'>
      <div className='container mx-auto'>
        {/* Section header with "See All" button */}
        <div className='flex justify-between items-center mb-6'>
          <h2 className='text-2xl font-bold text-gray-800'>New Products In Stock</h2>
          <Button
            onClick={handleSeeAll}
            variant='outline'
            className='text-primary border-primary hover:bg-primary hover:text-white'
          >
            See All Products
          </Button>
        </div>

        {/* Product grid - 5 per row on desktop, 2 per row on mobile */}
        <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4'>
          {displayProducts?.map((product) => {
            const { hasStock, isUnlimited } = getProductAvailability(product);
            return (
              <ProductCard
                key={product.id}
                id={product.id}
                name={product.name}
                image={product.images?.[0]?.url}
                price={product.finalPrice}
                oldPrice={product.discountPercentage > 0 ? product.basePrice : undefined}
                hasStock={hasStock}
                isUnlimited={isUnlimited}
                discount={product.discountPercentage}
                sku={product.sku}
              />
            );
          })}
        </div>

        {/* Optional: Show loading indicator when fetching more data */}
        {isFetching && !isLoading && (
          <div className='flex justify-center mt-6'>
            <div className='flex items-center space-x-2 text-gray-500'>
              <Loader2 className='h-4 w-4 animate-spin' />
              <span className='text-sm'>Updating products...</span>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
